import React, { Dispatch, MouseEvent, RefObject, useEffect, useRef, useState } from 'react';
import {
  AlertColor,
  Button,
  IconButton,
  Stack,
  TableCell,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import strings from 'common/Translation/Translate';
import Form, { FormDataModel } from 'components/Form';
import Notification from 'components/Notification';
import { VarianceChip, VarianceQtyHint } from 'components/InputField';
import { AddEditBookingAddOnForm } from './AddEditBookingAddOnForm';
import { ActualizationTableEmpty } from '../ActualizationTableEmpty';
import { ActualizationTaxCell } from '../ActualizationTaxCell';
import {
  actualizationTableSxFor,
  lineItemColumnWidths,
  mobileLineItemColumnWidths,
  useActualizationMobile,
} from '../actualizationTableLayout';
import DrawerMenu from 'components/DrawerMenu';
import { removeErrorFieldsFromValues } from 'utils/validators';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import Tabular from 'components/Tabular/Tabular';
import { FormComponent } from 'utils/validators/HelperComponents';
import { formatCurrency, getDrawerWidth } from 'utils/validators/HelperFunctions';
import { ToolTip } from 'components/Tooltip/ToolTip';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import EditIcon from '@mui/icons-material/Edit';
import { URLS } from 'utils/constants/urls';
import axiosInstance from 'utils/axios';

type TableComponentProps = {
  editedData: any;
  editRow: (idx: any) => void;
  deleteRow: (idx: any) => void;
  pastBooking: boolean;
  dropdownData: { label: string; value: string }[];
  taxGroupDropdownData: { label: string; value: string }[];
  noGuest: number;
  previewAddOns?: any[];
  previewVariances?: any[];
};

const qtyNumber = (value: unknown) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

const addOnLineAmount = (row: any) => {
  const qty = qtyNumber(row.actualQuantity);
  const rate = qtyNumber(row.price);
  const gross = rate * qty;
  const discount = qtyNumber(row.discount);
  let net = gross;
  if (discount && row.discountType) {
    net =
      row.discountType === 'PERCENT'
        ? Math.max(0, gross - (gross * discount) / 100)
        : Math.max(0, gross - discount);
  }
  const tax = row.addOnTax == null || row.addOnTax === '' ? 0 : qtyNumber(row.addOnTax);
  const serviceCharge =
    row.addOnServiceCharge == null || row.addOnServiceCharge === '' ? 0 : qtyNumber(row.addOnServiceCharge);
  return net + serviceCharge + tax;
};

const addOnRateLabel = (row: any) => {
  const formatted = formatCurrency(qtyNumber(row.price));
  const template =
    row.addOnType === 'PER_QUANTITY' ? strings.actualization_addon_rate_unit : strings.actualization_addon_rate_person;
  return String(strings.formatString(template, formatted));
};

const discountLabel = (row: any) => {
  if (row.discount == null || row.discount === '' || Number(row.discount) === 0) return '—';
  return row.discountType === 'PERCENT' ? `${row.discount}%` : formatCurrency(qtyNumber(row.discount));
};

const taxGroupLabel = (
  row: any,
  taxGroupDropdownData: { label: string; value: string }[],
  previewName?: string,
) =>
  previewName ??
  taxGroupDropdownData?.find(
    (opt: any) => String(opt.value) === String(row?.taxGroupId?.value ?? row?.taxGroupId),
  )?.label ??
  row?.selectedTaxGroup?.label ??
  row?.taxGroup?.label ??
  '—';

const varianceTone = (delta: number) => (delta > 0 ? 'positive' : delta < 0 ? 'negative' : 'neutral');

const AmountVarianceCell = ({ deltaAmount, deltaQty }: { deltaAmount: number; deltaQty: number }) => (
  <Stack alignItems="flex-end" spacing={0.25}>
    <VarianceChip label={formatCurrency(Math.abs(deltaAmount))} tone={varianceTone(deltaAmount)} />
    <VarianceQtyHint delta={deltaQty} />
  </Stack>
);

const QtyVarianceChip = ({ delta }: { delta: number }) => (
  <VarianceChip
    label={String(strings.formatString(strings.actualization_addon_qty_variance, Math.abs(delta)))}
    tone={varianceTone(delta)}
  />
);

const TableComponent = ({
  editedData,
  editRow,
  deleteRow,
  pastBooking,
  dropdownData,
  taxGroupDropdownData,
  previewAddOns,
  previewVariances,
}: TableComponentProps) => {
  const mobile = useActualizationMobile();
  const headers = [
    strings.description,
    strings.actualization_addon_qty,
    ...(mobile
      ? []
      : [strings.actualization_addon_pricing, strings.actualization_addon_discount, strings.actualization_addon_tax]),
    strings.actualization_addon_amount,
    ...(mobile ? [] : [strings.actualization_addon_variance]),
    ...(pastBooking ? [''] : []),
  ];
  const columnAlign = headers.map((_, i) => (i === 0 ? 'left' : 'right')) as Array<'left' | 'right'>;
  const totalAmount = editedData.reduce((sum: number, row: any, idx: number) => {
    const previewAmount = previewAddOns?.[idx]?.amount;
    return sum + (previewAmount == null ? addOnLineAmount(row) : qtyNumber(previewAmount));
  }, 0);

  return (
    <Tabular
      size="small"
      headerColor="secondary"
      rows={headers}
      columnAlign={columnAlign}
      columnWidths={mobile ? mobileLineItemColumnWidths(pastBooking) : lineItemColumnWidths(pastBooking)}
      sx={actualizationTableSxFor(mobile)}>
      {editedData.map((row: any, idx: number) => {
        const name = dropdownData?.find((i: any) => i.value === row.bookedAddOnId)?.label || row?.name || '—';
        const category = row.addOnCategoryName || '—';
        const bookedQty = qtyNumber(row.quantity);
        const actualQty = qtyNumber(row.actualQuantity);
        const previewLine = previewAddOns?.[idx];
        const previewVariance = previewVariances?.[idx];
        const amount = previewLine?.amount == null ? addOnLineAmount(row) : qtyNumber(previewLine.amount);
        return (
          <TableRow
            key={row.id ?? `${row.bookedAddOnId}-${idx}`}
            hover={pastBooking}
            onClick={pastBooking ? () => editRow(idx) : undefined}
            sx={
              pastBooking
                ? {
                    cursor: 'pointer',
                    '&:hover .actualization-delete-btn': { color: 'error.main' },
                  }
                : undefined
            }>
            <TableCell scope="row">
              <Stack spacing={0.125}>
                <Typography sx={{ fontSize: '13px', lineHeight: 1.25, fontWeight: 600 }}>
                  {name}
                </Typography>
                {category && category !== '—' ? (
                  <Typography color="text.secondary" sx={{ fontSize: '11px', lineHeight: 1.25 }}>
                    {category}
                  </Typography>
                ) : null}
              </Stack>
            </TableCell>
            <TableCell align="right">
              <Stack alignItems="flex-end" spacing={0.125}>
                <Typography sx={{ fontSize: '14px', lineHeight: 1.25, fontWeight: 500 }}>
                  {actualQty}
                </Typography>
                <Typography color="text.secondary" sx={{ fontSize: '10px', lineHeight: 1.25 }}>
                  {String(strings.formatString(strings.actualization_addon_booked_hint, bookedQty))}
                </Typography>
              </Stack>
            </TableCell>
            {!mobile && (
              <>
                <TableCell align="right">{addOnRateLabel(row)}</TableCell>
                <TableCell align="right">{discountLabel(row)}</TableCell>
                <TableCell align="right">
                  <ActualizationTaxCell
                    amount={previewLine?.addOnTax ?? row.addOnTax}
                    taxGroupName={taxGroupLabel(row, taxGroupDropdownData, previewLine?.taxGroupName)}
                  />
                </TableCell>
              </>
            )}
            <TableCell align="right">{formatCurrency(amount)}</TableCell>
            {!mobile && (
              <TableCell align="right">
                {previewVariance ? (
                  <AmountVarianceCell
                    deltaAmount={qtyNumber(previewVariance.deltaAmount)}
                    deltaQty={qtyNumber(previewVariance.deltaQuantity)}
                  />
                ) : (
                  <QtyVarianceChip delta={actualQty - bookedQty} />
                )}
              </TableCell>
            )}
            {pastBooking && (
              <TableCell align="right">
                <ToolTip title="Edit">
                  <IconButton
                    size="small"
                    sx={{ mr: 0.5, p: 0.25 }}
                    onClick={(e) => {
                      e.stopPropagation();
                      editRow(idx);
                    }}
                    aria-label="edit"
                    color="primary">
                    <EditIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </ToolTip>
                <ToolTip title="Delete">
                  <IconButton
                    size="small"
                    className="actualization-delete-btn"
                    sx={{ p: 0.25, '&:hover': { color: 'error.main' } }}
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteRow(idx);
                    }}
                    aria-label="delete"
                    color="primary">
                    <DeleteOutlineOutlinedIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </ToolTip>
              </TableCell>
            )}
          </TableRow>
        );
      })}
      <TableRow
        sx={{
          '& td': {
            fontWeight: 700,
            fontSize: '12px',
            lineHeight: 1.25,
            py: 0.75,
            px: 1,
            boxShadow: theme => `inset 0 1px 0 ${theme.palette.divider}`,
          },
        }}>
        <TableCell colSpan={mobile ? 2 : 5}>{strings.actualization_addon_total}</TableCell>
        <TableCell align="right">{formatCurrency(totalAmount)}</TableCell>
        {!mobile && <TableCell align="right" />}
        {pastBooking && <TableCell align="right" />}
      </TableRow>
    </Tabular>
  );
};

type AddEditBookingAddOnProps = {
  editedData: any;
  setData: Dispatch<any>;
  pastBooking: boolean;
  dropdownData: { label: string; value: string }[];
  taxGroupDropdownData: { label: string; value: string }[];
  noGuest?: any;
  addOnCategories: any;
  previewAddOns?: any[];
  previewVariances?: any[];
};

export const AddEditBookingAddOn: React.FC<AddEditBookingAddOnProps> = ({
  editedData = [],
  setData,
  pastBooking,
  dropdownData,
  taxGroupDropdownData,
  addOnCategories,
  noGuest,
  previewAddOns,
  previewVariances,
}) => {
  const theme = useTheme();
  const screenSizeDownSm = useMediaQuery(theme.breakpoints.down('sm'));
  const screenSizeDownMd = useMediaQuery(theme.breakpoints.down('md'));
  const screenSizeUpLg = useMediaQuery(theme.breakpoints.up('lg'));
  const [isPerGuest, setIsPerGuest] = useState<boolean>(false);
  const [openDrawer, setOpenDrawer] = useState<boolean>(false);
  const [pageResponse, setPageResponse] = useState<any>({});
  const [hasError, setHasError] = useState<boolean>(false);
  const [selectedRowId, setSelectedRowId] = useState<any>(null);
  const [discountDisabled, setDiscountDisabled] = useState<boolean>(true);
  const [filteredAddOnNames, setFilteredAddOnNames] = useState<{ label: string; value: string }[]>(dropdownData);
  const [message, setMessage] = useState<any>({
    display: false,
    severity: null,
    message: '',
  });

  const closeNotification = (value: boolean) => setMessage({ ...message, display: value });

  let formRef: RefObject<Form | null | undefined> = useRef();

  useEffect(() => {
    setFilteredAddOnNames(dropdownData);
  }, [dropdownData]);

  const fetchAddOnsByCategory = async (categoryId: number) => {
    try {
      const { data } = await axiosInstance.get(URLS.getAddOnsByCategory(categoryId));
      return data.map((addOn: any) => ({
        value: addOn.id,
        label: addOn.name,
      }));
    } catch (error) {
      console.error('Error fetching add-ons by category:', error);
      return dropdownData; // Fallback to all add-ons
    }
  };

  const handleSave = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    const { getFormData } = formRef.current as {
      getFormData: () => { formData: FormDataModel; isFormValid: boolean };
    };
    const { formData, isFormValid } = getFormData();
    const data = removeErrorFieldsFromValues(formData);
    setHasError(false);
    if (isFormValid) {
      if (data.discountType === 'FLAT' && String(data.discount ?? '').trim().length > 10) {
        setMessage({
          display: true,
          severity: 'error',
          message: strings.flatDiscountError,
        });
      } else if (data.discountType === 'PERCENT' && parseInt(formData.discount) > 100) {
        setMessage({
          display: true,
          severity: 'error',
          message: strings.percentDiscountError,
        });
      } else {
        const array = [...editedData];
        const myData = {
          ...data,
          bookedAddOnId: data.bookedAddOnId.value,
          addOnCategoryName: data.addOnCategory.label,
          actualQuantity: data.actualQuantity,
          selectedTaxGroup: data.taxGroup,
          taxGroupId: data.taxGroup?.value ?? data.taxGroupId ?? null,
        };
        if (selectedRowId !== null) {
          array[selectedRowId] = { ...array[selectedRowId], ...myData };
          setSelectedRowId(null);
        } else {
          array.push(myData);
        }
        setData(array);
        setOpenDrawer(false);
        setPageResponse({});
      }
    } else {
      setHasError(true);
    }
  };

  const handleClose = () => {
    setOpenDrawer(false);
    setPageResponse({});
    formRef.current?.resetForm();
    setSelectedRowId(null);
    setHasError(false);
    setFilteredAddOnNames(dropdownData);
  };

  const deleteRow = (idx: any) => {
    const deleteRow = JSON.parse(JSON.stringify(editedData));
    const deleteValue = deleteRow.filter((item: any, i: any) => i !== idx);
    setData(deleteValue);
  };

  const editRow = (idx: any) => {
    setSelectedRowId(idx);
    const editRow = JSON.parse(JSON.stringify(editedData));
    const editValue = editRow.find((item: any, i: any) => i === idx);
    setIsPerGuest(editValue.addOnType === 'PER_QUANTITY' ? true : false);
    setPageResponse({
      ...editValue,
      bookedAddOnId: {
        value: editValue.bookedAddOnId,
        label: dropdownData?.find((i: any) => i.value === editValue.bookedAddOnId)?.label,
      },
      addOnCategory: {
        value: editValue.addOnCategory,
        label: editValue.addOnCategoryName,
      },
      taxGroup: editValue.selectedTaxGroup ?? editValue.taxGroup,
    });
    setOpenDrawer(true);
  };

  const handleChange = async (field: string, value: any, formData: any) => {
    if (field === 'bookedAddOnId' && value) {
      const { data } = await axiosInstance(URLS.get_edit_delete_addOnsById(parseInt(value.value)));
      const matchedCategory = addOnCategories.find(
        (item: any) => item.value === (data.addOnCategory?.value ?? data.addOnCategory),
      );
      setIsPerGuest(data.addOnType.value === 'PER_QUANTITY' ? true : false);
      setPageResponse({
        ...data,
        bookedAddOnId: value,
        addOnType: data.addOnType.value,
        quantity: 0,
        actualQuantity: data.addOnType.value === 'PER_GUEST' ? noGuest : formData?.actualQuantity,
        discountType: formData.discountType,
        discount: formData.discount,
        hsnCode: data.hsnCode,
        taxGroup: data.taxGroup,
        addOnCategory: matchedCategory ?? '',
        addOnCategoryName: matchedCategory.label ?? '',
      });
    } else if (field === 'discountType') {
      if (value === '') setDiscountDisabled(true);
      else {
        setDiscountDisabled(false);
      }
    } else if (field === 'addOnCategory') {
      if (value?.value) {
        fetchAddOnsByCategory(value.value).then(filteredAddOns => setFilteredAddOnNames(filteredAddOns));
      } else {
        setFilteredAddOnNames([]);
      }
      setPageResponse({
        ...pageResponse,
        addOnCategory: value,
        addOnCategoryName: value?.label ?? '',
        bookedAddOnId: '',
      });
    }
  };

  return (
    <React.Fragment>
      {message.display && (
        <Notification
          isOpen={message.display}
          message={message.message}
          severity={message.severity as AlertColor}
          closeNotification={closeNotification}
          autoHideDuration={2000}
        />
      )}
      {(pastBooking || editedData?.length > 0) && (
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mt: 0.5, mb: 1.5, minHeight: 36, gap: 1 }}>
          <Typography color="primary" sx={{ fontSize: '1rem', fontWeight: 600, lineHeight: 1.3 }}>
            {strings.actualization_section_addons}
          </Typography>
          {pastBooking && (
            <Button
              onClick={() => setOpenDrawer(true)}
              variant="outlined"
              size="small"
              startIcon={<AddCircleOutlineIcon />}
              sx={{ minHeight: 36 }}>
              {strings.actualization_section_addons_add}
            </Button>
          )}
        </Stack>
      )}
      {editedData?.length ? (
        <TableComponent
          pastBooking={pastBooking}
          editedData={editedData}
          editRow={editRow}
          deleteRow={deleteRow}
          dropdownData={dropdownData}
          taxGroupDropdownData={taxGroupDropdownData}
          noGuest={noGuest}
          previewAddOns={previewAddOns}
          previewVariances={previewVariances}
        />
      ) : pastBooking ? (
        <ActualizationTableEmpty message={strings.actualization_section_addons_empty} />
      ) : null}
      <DrawerMenu
        width={getDrawerWidth(screenSizeDownSm, screenSizeDownMd, screenSizeUpLg)}
        open={openDrawer}
        handleClose={handleClose}
        closeButton={true}
        title={strings.booking_addons}>
        <FormComponent
          hasError={hasError}
          formRef={formRef}
          handleChange={handleChange}
          pageResponse={pageResponse}
          handleSave={handleSave}
          formModel={AddEditBookingAddOnForm(
            strings,
            filteredAddOnNames,
            selectedRowId !== null,
            discountDisabled,
            addOnCategories,
            taxGroupDropdownData,
          )}
        />
      </DrawerMenu>
    </React.Fragment>
  );
};
